import { useEffect, useMemo, useState } from 'react';
import { swapsOf } from '../engine/imageSwap';
import { buildStrippedPreview, replacementsOf } from '../engine/stripReplaced';
import { usePdfEditorStore } from '../state/usePdfEditorStore';

const NONE = { doc: null, stripped: new Set() };

/**
 * The document the canvas should draw: the original, or a copy with the
 * replaced paragraphs taken out.
 *
 * Rebuilding means re-parsing and re-saving the whole file, so it is keyed on
 * the *geometry of the replacements* and nothing else. Typing into a box does
 * not change that key — the words being typed are drawn by the overlay, not by
 * this render — so a rebuild happens when a paragraph is replaced, moved or
 * removed, and not on a keystroke.
 *
 * Until the rebuild lands the old render stays up, patch and all. That is the
 * right way round: the patch is the safe state, and it is what the export falls
 * back to whenever the strip does not take.
 */
export function useStrippedPreview() {
    const sourceBytes = usePdfEditorStore((s) => s.sourceBytes);
    const pages = usePdfEditorStore((s) => s.pages);
    const objectsByUid = usePdfEditorStore((s) => s.objectsByUid);
    // Draft view reads the same document with the pictures blanked, so the two
    // reasons to re-render the page go through one builder and one cache.
    const hideImages = usePdfEditorStore((s) => s.displayMode === 'draft');

    const key = useMemo(() => pages.map((p) => {
        const r = replacementsOf(objectsByUid[p.uid]);
        // A swapped picture keys on its id alone: its rect never moves, and the
        // src is a data URL far too long to put in a dependency.
        const swaps = swapsOf(objectsByUid[p.uid]).map((o) => o.id).join('+');
        if (!r.length && !swaps) return '';
        return [
            r.map((o) => [
                o.id,
                Math.round(o.replaces.x), Math.round(o.replaces.y),
                Math.round(o.replaces.w), Math.round(o.replaces.h),
            ].join(',')).join('|'),
            swaps,
        ].filter(Boolean).join('#');
    }).join(';'), [pages, objectsByUid]);

    const [state, setState] = useState(NONE);

    useEffect(() => {
        if (!hideImages && !key.replace(/[;|#+]/g, '')) {
            setState((prev) => (prev === NONE ? prev : NONE));
            return undefined;
        }
        let alive = true;
        let built = null;
        buildStrippedPreview(sourceBytes, pages, objectsByUid, { hideImages })
            .then((result) => {
                if (!alive) {
                    result?.doc?.destroy?.();
                    return;
                }
                built = result;
                setState(result || NONE);
            })
            // A document this cannot re-save is one the canvas keeps drawing
            // whole, with the patch over it — exactly as before.
            .catch(() => { if (alive) setState(NONE); });

        return () => {
            alive = false;
            built?.doc?.destroy?.();
        };
        // `pages`/`objectsByUid` are read through the key: including them here
        // would rebuild on every keystroke, which is the thing to avoid.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, sourceBytes, hideImages]);

    return state;
}
